/**
 * Error codes the recorder throws. Catch a {@link RecorderError} and switch on
 * `.code` for programmatic handling.
 *
 * Only codes the recorder actually emits are listed here.
 */
export enum RecorderErrorCode {
  // Configuration — invalid construction options.
  MISSING_API_URL = 'MISSING_API_URL',
  MISSING_API_KEY = 'MISSING_API_KEY',
  INVALID_API_URL = 'INVALID_API_URL',
  INVALID_FLUSH_INTERVAL = 'INVALID_FLUSH_INTERVAL',
  INVALID_SAMPLE_INTERVAL = 'INVALID_SAMPLE_INTERVAL',
  INVALID_CHECKOUT_INTERVAL = 'INVALID_CHECKOUT_INTERVAL',
  INVALID_WS_TIMEOUT = 'INVALID_WS_TIMEOUT',
  INVALID_WS_RECONNECT_ATTEMPTS = 'INVALID_WS_RECONNECT_ATTEMPTS',
  INVALID_WS_RECONNECT_DELAY = 'INVALID_WS_RECONNECT_DELAY',

  // Lifecycle.
  ALREADY_STOPPED = 'ALREADY_STOPPED',

  // Consent.
  CONSENT_DENIED = 'CONSENT_DENIED',
}

/** Human-readable default message for each code (overridable per throw). */
export const ERROR_MESSAGES: Record<RecorderErrorCode, string> = {
  [RecorderErrorCode.MISSING_API_URL]: 'apiUrl is required',
  [RecorderErrorCode.MISSING_API_KEY]: 'apiKey is required',
  [RecorderErrorCode.INVALID_API_URL]: 'apiUrl must be a valid absolute URL',
  [RecorderErrorCode.INVALID_FLUSH_INTERVAL]: 'flushIntervalMs must be a positive number',
  [RecorderErrorCode.INVALID_SAMPLE_INTERVAL]: 'Sample interval must be a positive number',
  [RecorderErrorCode.INVALID_CHECKOUT_INTERVAL]: 'checkoutEveryNms must be a positive number',
  [RecorderErrorCode.INVALID_WS_TIMEOUT]: 'wsOpenTimeoutMs must be a positive number',
  [RecorderErrorCode.INVALID_WS_RECONNECT_ATTEMPTS]:
    'wsReconnectAttempts must be a non-negative number',
  [RecorderErrorCode.INVALID_WS_RECONNECT_DELAY]:
    'wsReconnectBaseDelayMs must be a positive number',
  [RecorderErrorCode.ALREADY_STOPPED]: 'Recorder has been stopped and cannot be restarted',
  [RecorderErrorCode.CONSENT_DENIED]:
    'Recording consent denied. User has opted out of session recording.',
};

/** Error carrying a {@link RecorderErrorCode} plus optional context. */
export class RecorderError extends Error {
  constructor(
    public readonly code: RecorderErrorCode,
    message?: string,
    public readonly context?: unknown
  ) {
    super(message ?? ERROR_MESSAGES[code]);
    this.name = 'RecorderError';
  }

  toString(): string {
    return `[${this.code}] ${this.message}`;
  }
}

/**
 * Error carrying the HTTP status from a failed backend call, so callers can
 * branch on the status code instead of pattern-matching the message string.
 * Every transport / session client throws this on a non-OK response.
 */
export class HttpError extends Error {
  constructor(
    public readonly status: number,
    message: string
  ) {
    super(message);
    this.name = 'HttpError';
  }
}

/** True when `err` represents an HTTP 401/403 (terminal auth failure). */
export function isAuthError(err: unknown): boolean {
  return err instanceof HttpError && (err.status === 401 || err.status === 403);
}

/** Returns the HTTP status for `err` if it is an {@link HttpError}, else null. */
export function httpStatusOf(err: unknown): number | null {
  return err instanceof HttpError ? err.status : null;
}

/** Returns the {@link RecorderErrorCode} for `err` if it is a {@link RecorderError}, else null. */
export function getErrorCode(error: unknown): RecorderErrorCode | null {
  return error instanceof RecorderError ? error.code : null;
}
